import React, { useState, useEffect } from 'react';
import { tr } from 'date-fns/locale';
import {
  Calendar,
  FileCheck,
  Target,
  Building2,
  Wallet,
  Clock,
  Users,
  BookOpen,
  ListTodo,
  Settings
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

interface Stats {
  pendingTasksCount: number;
  completedTasksCount: number;
  totalUniversities: number;
  pendingAccountingCount: number;
  tasks: any[];
}

interface OperationsDashboardProps {
  onNavigate?: (tab: string) => void;
}

const OperationsDashboard: React.FC<OperationsDashboardProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({
    pendingTasksCount: 0,
    completedTasksCount: 0,
    totalUniversities: 0,
    pendingAccountingCount: 0,
    tasks: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [tasksRes, universitiesRes, installmentsRes] = await Promise.all([
        supabase
          .from('tasks')
          .select('*')
          .eq('branch_id', user?.branchId)
          .order('created_at', { ascending: false })
          .limit(10),
        supabase.from('universities').select('id', { count: 'exact', head: true }),
        supabase.from('installments').select('id, status').eq('status', 'pending').eq('branch_id', user?.branchId)
      ]);

      let pendingCount = 0;
      let completedCount = 0;
      let upcomingTasks: any[] = [];

      if (tasksRes.data) {
        pendingCount = tasksRes.data.filter(t => !t.completed).length;
        completedCount = tasksRes.data.filter(t => t.completed).length;
        upcomingTasks = tasksRes.data.filter(t => !t.completed).slice(0, 5); // Take top 5 pending
      }

      // Pending Accounting (Pending Installments Count)
      const pendingAccounting = installmentsRes.data ? installmentsRes.data.length : 0;

      setStats({
        pendingTasksCount: pendingCount,
        completedTasksCount: completedCount,
        totalUniversities: universitiesRes.count || 0,
        pendingAccountingCount: pendingAccounting,
        tasks: upcomingTasks
      });

    } catch (error) {
      console.error('Dashboard verisi alınamadı:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const quickActions = [
    {
      title: 'Öğrenciler',
      icon: Users,
      action: () => onNavigate && onNavigate('operations-leads'),
      color: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
      desc: 'Tüm öğrenci listesi'
    },
    {
      title: 'Üniversiteler',
      icon: Building2,
      action: () => onNavigate && onNavigate('universities'),
      color: 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400',
      desc: 'Anlaşmalı kurumlar'
    },
    {
      title: 'Tercihler',
      icon: BookOpen,
      action: () => onNavigate && onNavigate('preferences'),
      color: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
      desc: 'Bölüm ve okul tercihleri'
    },
    {
      title: 'Görevler',
      icon: ListTodo,
      action: () => onNavigate && onNavigate('tasks'),
      color: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
      desc: 'Yapılacak işler'
    }
  ];

  return (
    <div className="p-8 space-y-8 bg-gray-50/50 dark:bg-gray-950 min-h-screen font-sans">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold text-gray-800 dark:text-gray-100 tracking-tight">
            Operasyon
            <span className="text-blue-500">.</span>
          </h1>
          <div className="flex items-center gap-2 mt-2 text-gray-500 dark:text-gray-400 font-medium">
            <Calendar className="w-4 h-4" />
            <span>{format(new Date(), 'd MMMM yyyy, EEEE', { locale: tr })}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Görevler Kartı */}
        <div className="relative overflow-hidden group bg-white dark:bg-gray-800 rounded-3xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:shadow-emerald-100/50 dark:hover:shadow-emerald-900/20 transition-all duration-500">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 dark:bg-emerald-900/20 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/30"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform duration-500">
                <FileCheck className="w-6 h-6" />
              </div>
              <span className="text-emerald-600 dark:text-emerald-400 text-sm font-bold bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1 rounded-full">
                Görevler
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-4xl font-black text-gray-800 dark:text-white tracking-tight">
                {stats.pendingTasksCount}
              </p>
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                {stats.completedTasksCount} tamamlandı
              </div>
            </div>
          </div>
        </div>

        {/* Toplam Üniversiteler Kartı */}
        <div className="relative overflow-hidden group bg-white dark:bg-gray-800 rounded-3xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:shadow-violet-100/50 dark:hover:shadow-violet-900/20 transition-all duration-500">
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-50 dark:bg-violet-900/20 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-violet-100 dark:group-hover:bg-violet-900/30"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="w-12 h-12 bg-violet-50 dark:bg-violet-900/30 rounded-2xl flex items-center justify-center text-violet-600 dark:text-violet-400 group-hover:scale-110 transition-transform duration-500">
                <Building2 className="w-6 h-6" />
              </div>
              <span className="text-violet-600 dark:text-violet-400 text-sm font-bold bg-violet-50 dark:bg-violet-900/30 px-3 py-1 rounded-full">
                Üniversiteler
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-4xl font-black text-gray-800 dark:text-white tracking-tight">
                {stats.totalUniversities}
              </p>
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                Toplam hizmet verilen
              </div>
            </div>
          </div>
        </div>

        {/* Muhasebe Kartı */}
        <div className="relative overflow-hidden group bg-white dark:bg-gray-800 rounded-3xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:shadow-orange-100/50 dark:hover:shadow-orange-900/20 transition-all duration-500">
          <div className="absolute top-0 right-0 w-32 h-32 bg-orange-50 dark:bg-orange-900/20 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="w-12 h-12 bg-orange-50 dark:bg-orange-900/30 rounded-2xl flex items-center justify-center text-orange-600 dark:text-orange-400 group-hover:scale-110 transition-transform duration-500">
                <Wallet className="w-6 h-6" />
              </div>
              <span className="text-orange-600 dark:text-orange-400 text-sm font-bold bg-orange-50 dark:bg-orange-900/30 px-3 py-1 rounded-full">
                Muhasebe
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-4xl font-black text-gray-800 dark:text-white tracking-tight">
                {stats.pendingAccountingCount}
              </p>
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                Bekleyen kişi sayısı
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Bekleyen Görevler Listesi */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 p-8 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">Bekleyen Görevler</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Tamamlanması gereken işler</p>
            </div>
            <button
              onClick={() => onNavigate && onNavigate('tasks')}
              className="px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-semibold transition-colors">
              Tümünü Gör
            </button>
          </div>
          <div className="space-y-4">
            {stats.tasks.length === 0 ? (
              <div className="text-gray-500 dark:text-gray-400 text-center py-4">Bekleyen görev bulunmuyor.</div>
            ) : (
              stats.tasks.map((task) => (
                <div key={task.id} className="group flex items-start gap-4 p-4 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700/50 border border-transparent hover:border-gray-100 dark:hover:border-gray-700 transition-all cursor-pointer">
                  <div className={`mt-1 w-3 h-3 rounded-full border-2 ${task.priority === 'high' ? 'border-red-400 bg-red-400' : (task.priority === 'medium' ? 'border-orange-400 bg-orange-400' : 'border-green-400 bg-green-400')}`}></div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {task.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      Bitiş: {format(new Date(task.due_date), 'd MMMM', { locale: tr })}
                    </p>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-500 dark:group-hover:bg-blue-900/20 transition-all">
                    <Target className="w-4 h-4" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Hızlı İşlemler - Yeni Alan */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 p-8 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">Hızlı İşlemler</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Sık kullanılan sayfalara hızlı erişim
              </p>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {quickActions.map((item, index) => (
              <button
                key={index}
                onClick={item.action}
                className="group flex flex-col items-start p-5 rounded-2xl bg-gray-50 dark:bg-gray-700/30 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-lg transition-all duration-300"
              >
                <div className={`p-3 rounded-xl mb-3 transition-colors ${item.color}`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-base font-bold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-left">
                  {item.desc}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationsDashboard;
